import { Tooltip, TooltipContent, TooltipTrigger } from "@elit-hair/elit-ui";
import { motion } from "framer-motion";
import { t } from "i18next";
import { useEffect, useRef } from "react";
import { TbPlayerStop, TbSend } from "react-icons/tb";

import { cn } from "@/lib/utils";
import { scaleIn, withTransition } from "@/utils/animations";

type ChatInputProps = {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isLoading?: boolean;
    placeholder?: string;
    className?: string;
    isStreaming?: boolean;
    onStop?: () => void;
};

export function ChatInput({
    value,
    onChange,
    onSubmit,
    isLoading = false,
    placeholder = t("type_your_message"),
    className,
    isStreaming = false,
    onStop,
}: ChatInputProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (value.trim() && !isLoading) {
            onSubmit(e);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (value.trim() && !isLoading) {
                onSubmit();
            }
        }
    };

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
    }, [value]);

    // Auto-focus on mount
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }, []);

    const handleTextareaClick = () => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    return (
        <motion.form
            onSubmit={handleSubmit}
            className={cn("p-4 relative", className)}
            {...withTransition(scaleIn, { duration: 0.2 })}
        >
            <div className="flex items-end gap-4 relative z-10">
                <div className="flex-1 relative cursor-text pointer-events-auto" onClick={handleTextareaClick}>
                    <textarea
                        ref={textareaRef}
                        value={value}
                        onChange={onChange}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        disabled={false}
                        className={cn(
                            "w-full resize-none border-none outline-none bg-transparent",
                            "placeholder:text-gray-500 dark:placeholder:text-white/60 text-gray-900 dark:text-white font-medium",
                            "min-h-[44px] max-h-[120px] p-2",
                            "focus:ring-0 focus:outline-none",
                            "transition-all duration-200",
                            "cursor-text pointer-events-auto",
                            isLoading && "opacity-50"
                        )}
                        rows={1}
                    />
                </div>

                <div className="flex items-center gap-2">
                    {/* Stop Button (only show when streaming) */}
                    {isStreaming && onStop && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <motion.button
                                    type="button"
                                    onClick={onStop}
                                    {...withTransition(scaleIn, { duration: 0.2 })}
                                    className={cn(
                                        "p-3 rounded-full transition-all duration-300 relative overflow-hidden group",
                                        "bg-gradient-to-br from-red-500 via-orange-500 to-red-600",
                                        "text-white shadow-xl border-none",
                                        "hover:shadow-2xl hover:scale-105"
                                    )}
                                >
                                    <TbPlayerStop className="w-5 h-5 relative z-10 drop-shadow-sm group-hover:scale-110 transition-transform duration-200" />
                                </motion.button>
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                className="bg-black/80 backdrop-blur-sm border border-white/20 text-white"
                            >
                                {t("stop_streaming")}
                            </TooltipContent>
                        </Tooltip>
                    )}

                    {/* Send Button */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <motion.button
                                type="submit"
                                {...withTransition(scaleIn, { duration: 0.2 })}
                                disabled={!value.trim() || isLoading || isStreaming}
                                className={cn(
                                    "p-3 rounded-full transition-all duration-300 relative overflow-hidden group",
                                    "bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500",
                                    "text-white shadow-xl border-none",
                                    "hover:shadow-2xl hover:scale-105",
                                    "disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100",
                                    "disabled:shadow-lg"
                                )}
                            >
                                <TbSend className="w-5 h-5 relative z-10 drop-shadow-sm group-hover:scale-110 transition-transform duration-200" />
                            </motion.button>
                        </TooltipTrigger>
                        <TooltipContent
                            side="top"
                            className="bg-black/80 backdrop-blur-sm border border-white/20 text-white"
                        >
                            {t("send_message")}
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>
        </motion.form>
    );
}
